import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, CalendarOff } from "lucide-react";
import api from "@/lib/api";

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function AvailabilityPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/availability?days=60");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.put("/admin/availability", body);
      await load();
      toast.success("Disponibilités mises à jour");
    } catch { toast.error("Enregistrement impossible"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-sm text-ink-soft">Chargement…</div>;

  const blockedList = [];
  data.days.forEach((d) => d.slots.forEach((s) => { if (s.reason === "Indisponible") blockedList.push({ date: d.date, creneau: s.creneau }); }));

  const toggleSlot = (date, creneau, blocked) => {
    const next = blocked
      ? blockedList.filter((b) => !(b.date === date && b.creneau === creneau))
      : [...blockedList, { date, creneau }];
    patch({ blocked: next });
  };

  const toggleDay = (date, closedByAdmin) => {
    const dayBlocks = data.days.find((d) => d.date === date)?.slots.map((s) => ({ date, creneau: s.creneau })) || [];
    const others = blockedList.filter((b) => b.date !== date);
    patch({ blocked: closedByAdmin ? others : [...others, ...dayBlocks] });
  };

  const toggleWeekday = (i) => {
    const cur = data.closed_weekdays || [];
    patch({ closed_weekdays: cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i] });
  };

  return (
    <div className="space-y-8" data-testid="availability-panel">
      <div>
        <h1 className="font-display text-3xl md:text-4xl text-anthracite">Disponibilités des rendez-vous</h1>
        <p className="mt-3 text-sm text-ink-soft max-w-2xl leading-relaxed">
          Un créneau réservé par un client devient automatiquement indisponible. Vous pouvez en plus fermer un jour
          entier ou un créneau précis : il apparaîtra grisé et non réservable côté client.
        </p>
      </div>

      <div className="bg-white border border-black/10 rounded-rama-md p-6">
        <div className="font-mono-caption text-ink-soft mb-4">Jours de fermeture hebdomadaire</div>
        <div className="flex flex-wrap gap-2">
          {JOURS.map((j, i) => {
            const off = (data.closed_weekdays || []).includes(i);
            return (
              <button key={j} data-testid={`weekday-${i}`} disabled={busy} onClick={() => toggleWeekday(i)}
                className={`px-4 py-2 rounded-full border text-sm transition-colors ${off ? "bg-anthracite text-ivory border-anthracite" : "border-black/20 hover:border-anthracite text-anthracite"}`}>
                {off ? <CalendarOff size={13} className="inline mr-1.5" /> : null}{j}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-black/10 rounded-rama-md p-6 overflow-x-auto">
        <div className="font-mono-caption text-ink-soft mb-4">60 prochains jours — cliquez pour ouvrir ou fermer un créneau</div>
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-ink-soft">
              <th className="py-2 pr-4 font-normal">Jour</th>
              {data.creneaux.map((c) => <th key={c} className="py-2 px-2 font-normal whitespace-nowrap">{c}</th>)}
              <th className="py-2 pl-2 font-normal">Journée</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {data.days.map((d) => {
              const closedWeek = (data.closed_weekdays || []).includes(d.weekday);
              const allBlocked = d.slots.every((s) => s.reason === "Indisponible");
              return (
                <tr key={d.date} className={closedWeek ? "opacity-40" : ""}>
                  <td className="py-2 pr-4 whitespace-nowrap text-anthracite">
                    {new Date(`${d.date}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
                  </td>
                  {d.slots.map((s) => {
                    const booked = s.reason === "Déjà réservé";
                    const blocked = s.reason === "Indisponible";
                    return (
                      <td key={s.creneau} className="py-2 px-2">
                        <button data-testid={`slot-${d.date}-${s.creneau}`} disabled={booked || closedWeek || busy}
                          onClick={() => toggleSlot(d.date, s.creneau, blocked)}
                          className={`w-full px-2 py-1.5 rounded-rama-sm text-xs transition-colors ${
                            booked ? "bg-bordeaux-700/10 text-bordeaux-700 cursor-not-allowed"
                            : blocked ? "bg-anthracite text-ivory"
                            : "bg-black/[0.04] text-ink-soft hover:bg-black/10"}`}>
                          {booked ? "Réservé" : blocked ? <><X size={11} className="inline" /> Fermé</> : <><Check size={11} className="inline" /> Libre</>}
                        </button>
                      </td>
                    );
                  })}
                  <td className="py-2 pl-2">
                    <button data-testid={`day-toggle-${d.date}`} disabled={closedWeek || busy} onClick={() => toggleDay(d.date, allBlocked)}
                      className="text-xs text-bordeaux-700 hover:underline whitespace-nowrap">
                      {allBlocked ? "Réouvrir" : "Fermer"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
